// =============================================
// FACe Submission Service (Punto General de Entrada)
// Spanish public sector e-invoicing: https://face.gob.es
// =============================================

// FACe estado codes (numeric per FACe API docs)
// 1200=Registrada REC, 1300=Registrada RCF, 2400=Contabilizada, 2500=Pagada, 2600=Rechazada, 3100=Anulada
export type FACeEstadoFactura = '1200' | '1300' | '2400' | '2500' | '2600' | '3100' | 'PENDING';

export interface FACeSubmission {
  facturae_xml: string;
  oficina_contable: string;
  organo_gestor: string;
  unidad_tramitadora: string;
  email_notificacion?: string;
}

export interface FACeSubmissionResult {
  success: boolean;
  numeroRegistro?: string;
  estado?: FACeEstadoFactura;
  estadoDescripcion?: string;
  fechaRegistro?: string;
  error?: { codigo: string; descripcion: string };
}

export interface FACeStatusResult {
  success: boolean;
  estado?: FACeEstadoFactura;
  estadoDescripcion?: string;
  motivo?: string;
  tramitacion?: { codigo: string; descripcion: string; motivo?: string };
  error?: { codigo: string; descripcion: string };
}

export function mapFACeEstadoDescripcion(estado: FACeEstadoFactura): string {
  const descriptions: Record<FACeEstadoFactura, string> = {
    '1200': 'Registrada en REC (Registro Electrónico Común)',
    '1300': 'Registrada en RCF (Registro Contable de Facturas)',
    '2400': 'Contabilizada la obligación reconocida',
    '2500': 'Pagada',
    '2600': 'Rechazada',
    '3100': 'Anulada',
    'PENDING': 'Pendiente de envío',
  };
  return descriptions[estado] ?? `Estado desconocido: ${estado}`;
}

export function mapFACeEstadoToInternal(
  estado: FACeEstadoFactura
): 'pendiente' | 'registrada' | 'aceptada' | 'rechazada' | 'pagada' {
  switch (estado) {
    case 'PENDING': return 'pendiente';
    case '1200':
    case '1300': return 'registrada';
    case '2400': return 'aceptada';
    case '2500': return 'pagada';
    case '2600':
    case '3100': return 'rechazada';
    default: return 'pendiente';
  }
}

function validateDIR3Code(code: string, fieldName: string): string | null {
  if (!code || code.trim().length === 0) return `${fieldName} es requerido`;
  // DIR3: alphanumeric 3-20 chars (e.g. "L01280796", "EA0028698")
  if (!/^[A-Z0-9]{3,20}$/i.test(code.trim())) {
    return `${fieldName} no tiene formato DIR3 válido: ${code}`;
  }
  return null;
}

/**
 * STUB: Submit invoice to FACe.
 * Uses env var FACE_API_URL. Validates DIR3 codes, logs request.
 */
export async function submitToFACe(
  submission: FACeSubmission
): Promise<FACeSubmissionResult> {
  const apiUrl = process.env.FACE_API_URL || 'https://webservice.face.gob.es/facturassrcv2';

  const validationErrors: string[] = [];
  const ocError = validateDIR3Code(submission.oficina_contable, 'Oficina Contable');
  const ogError = validateDIR3Code(submission.organo_gestor, 'Órgano Gestor');
  const utError = validateDIR3Code(submission.unidad_tramitadora, 'Unidad Tramitadora');
  if (ocError) validationErrors.push(ocError);
  if (ogError) validationErrors.push(ogError);
  if (utError) validationErrors.push(utError);

  if (validationErrors.length > 0) {
    return {
      success: false,
      error: { codigo: 'VALIDATION_ERROR', descripcion: validationErrors.join('; ') },
    };
  }

  if (!submission.facturae_xml || submission.facturae_xml.trim().length === 0) {
    return {
      success: false,
      error: { codigo: 'MISSING_XML', descripcion: 'El XML de Facturae es requerido para FACe' },
    };
  }

  console.log('[face-service] STUB: submitToFACe called', {
    apiUrl,
    xmlLength: submission.facturae_xml.length,
    oficina_contable: submission.oficina_contable,
    organo_gestor: submission.organo_gestor,
    unidad_tramitadora: submission.unidad_tramitadora,
  });

  const mockNumeroRegistro = `FACE${Date.now()}${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

  return {
    success: true,
    numeroRegistro: mockNumeroRegistro,
    estado: '1200',
    estadoDescripcion: mapFACeEstadoDescripcion('1200'),
    fechaRegistro: new Date().toISOString(),
  };
}

/**
 * STUB: Check status of previously submitted FACe invoice.
 * Uses env var FACE_API_URL.
 */
export async function checkFACeStatus(
  numeroRegistro: string
): Promise<FACeStatusResult> {
  const apiUrl = process.env.FACE_API_URL || 'https://webservice.face.gob.es/facturassrcv2';

  if (!numeroRegistro || numeroRegistro.trim().length === 0) {
    return {
      success: false,
      error: { codigo: 'MISSING_REGISTRO', descripcion: 'Número de registro requerido' },
    };
  }

  console.log('[face-service] STUB: checkFACeStatus called', { apiUrl, numeroRegistro });

  return {
    success: true,
    estado: '1300',
    estadoDescripcion: mapFACeEstadoDescripcion('1300'),
    tramitacion: {
      codigo: '1300',
      descripcion: 'Registrada en RCF (Registro Contable de Facturas)',
    },
  };
}
